import {
  NeedsClusterRemovedPulledClusterPhotoDto,
  needsClusterRemovedPulledClusterPhotoValidationSchema,
  NeedsDriversLicensePhotoUploadedDriversLicensePhotoDto,
  NeedsToBeMovedToStagingWarrantyCertPhotoDto,
  vehicleCheckInManufacturersLabelPhotoValidationSchema
} from 'dtos'
import { useEffect, useState } from 'react'
import * as controllers from 'controllers'
import { AddAPhoto, HideImage, QuestionMark } from '@mui/icons-material'
import { Dialog, DialogContent, Grid, DialogTitle, Button, TextField, DialogActions, Typography } from '@mui/material'
import { ExplanationAccordion, GreenText } from 'components'
import { Formik } from 'formik'
import { NavigateBefore as NavigateBeforeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'

interface DriversLicensePhotoDialogProps {
  initialValues: NeedsDriversLicensePhotoUploadedDriversLicensePhotoDto
  isLoading: boolean
  onClose: () => void
  onDelete: () => void
  onSave: (values: NeedsDriversLicensePhotoUploadedDriversLicensePhotoDto) => void
  open: boolean
  outboundTripGuid: string // Used to preview image
}

export default function DriversLicensePhotoDialog({
  initialValues,
  isLoading,
  onClose,
  onDelete,
  onSave,
  open,
  outboundTripGuid
}: DriversLicensePhotoDialogProps) {
  const [src, setSrc] = useState<string>('')

  useEffect(() => {
    if (initialValues.driversLicensePhotoFileName && outboundTripGuid) {
      controllers.outboundTrips
        .getNeedsDriversLicensePhotoUploadedPhotoBase64(outboundTripGuid, initialValues.driversLicensePhotoFileName)
        .then(base64 => {
          setSrc(`data:image/${initialValues.driversLicensePhotoFileName!.split('.').pop()};base64,${base64}`)
        })
    } else {
      setSrc('')
    }
  }, [initialValues.driversLicensePhotoFileName, outboundTripGuid])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Drivers License Photo Capture</DialogTitle>

                  <Grid item xs={12}>
                    <img
                      alt='Drivers License'
                      src={values.driversLicensePhotoFile ? URL.createObjectURL(values.driversLicensePhotoFile) : src}
                      style={{ width: '100%' }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    {errors.driversLicensePhotoFile && (
                      <Typography color='error' variant='body2'>
                        {errors.driversLicensePhotoFile}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <input
                      accept='image/png, image/jpeg'
                      capture='environment'
                      hidden
                      id='fileInput'
                      type='file'
                      onChange={e => {
                        if (e.target.files && e.target.files.length > 0) {
                          setFieldValue('driversLicensePhotoFile', e.target.files[0])
                        }
                      }}
                    />

                    <Button
                      color='primary'
                      disabled={isSubmitting || isLoading}
                      endIcon={<AddAPhoto />}
                      fullWidth
                      onClick={() => {
                        document.getElementById('fileInput')?.click()
                      }}
                      variant='outlined'
                    >
                      BROWSE OR TAKE PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={isSubmitting || isLoading}
                      endIcon={<HideImage />}
                      fullWidth
                      onClick={() => {
                        if (window.confirm('Are you sure you would like to delete the uploaded Drivers License Photo?')) {
                          onDelete()
                        }
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Photo Previewer:**

Displays a preview of the uploaded photo.

If no photo is uploaded, nothing will be displayed.

**BROWSE OR TAKE PHOTO Button:**

Clicking this button opens the user’s camera app, allowing
them to:

Take a new photo and upload it.

Upload an existing photo from their device.

**DELETE UPLOADED PHOTO Button:**

Clicking this button will:

Clear/delete the currently uploaded photo.

Remove it from the Photo Previewer.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not
save any changes

**SAVE Button:**

Clicking this button will:

Save the uploaded photo to the database and close the
dialog

                      `}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button
                  color='inherit'
                  disabled={isSubmitting || isLoading}
                  startIcon={<NavigateBeforeIcon />}
                  onClick={onClose}
                  variant='text'
                >
                  CANCEL
                </Button>

                <Button
                  color='primary'
                  disabled={isSubmitting || isLoading}
                  endIcon={<NavigateNextIcon />}
                  type='submit'
                  variant='text'
                >
                  SUBMIT
                </Button>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
